import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, videos } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'hot');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeCategory);

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'hot') return videos.slice(0, pageSize);
    return videos.filter(v => v.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredVideos.length / pageSize);
  const displayVideos = filteredVideos.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const catTitle = currentCategory ? `${currentCategory.name} - 黄瓜视频在线观看` : '视频分类 - 黄瓜视频在线观看';
  const catDesc = currentCategory ? `黄瓜视频${currentCategory.name}频道，${currentCategory.description}，随时随地在线观看高清视频。` : '黄瓜视频分类浏览，涵盖电影、电视剧、综艺、动漫、纪录片、体育等海量视频内容。';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: catTitle,
    description: catDesc,
    url: `https://bicasual.cn/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黄瓜视频',
      url: 'https://bicasual.cn',
    },
  };

  return (
    <>
      <SEOHead
        title={catTitle}
        description={catDesc}
        keywords={`黄瓜视频${currentCategory?.name || '视频'},在线观看,高清视频,免费播放`}
        canonicalUrl={`https://bicasual.cn/category/${activeCategory}`}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: currentCategory?.name || '分类' },
          ]} />

          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {currentCategory?.name || '视频分类'}
            </h1>
            <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            <Link
              to="/category/hot"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === 'hot'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              热门推荐
            </Link>
            {categories.filter(c => c.slug !== 'hot').map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {displayVideos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {displayVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40 cursor-pointer"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === i + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40 cursor-pointer"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-video-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}